import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { injectSchema, getBreadcrumbSchema, getWebPageSchema, getBaseUrl } from '../../utils/seo';

export default function APropos() {
  useEffect(() => {
    const baseUrl = getBaseUrl();
    
    // Inject Schema.org structured data
    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Spark\'Up',
      url: baseUrl,
      logo: `${baseUrl}/logo.png`,
      description: 'Service professionnel de nettoyage de canapés, fauteuils, matelas et tissus d\'intérieur à Lausanne',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Lausanne',
        addressRegion: 'Vaud',
        addressCountry: 'CH'
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+41782036539',
        contactType: 'customer service',
        areaServed: 'CH',
        availableLanguage: ['French']
      }
    };

    const schemas = [
      getWebPageSchema(
        'À Propos - Spark\'Up',
        'Découvrez Spark\'Up, votre partenaire de confiance pour un nettoyage professionnel à Lausanne'
      ),
      organizationSchema,
      getBreadcrumbSchema([
        { name: 'Accueil', url: '/' },
        { name: 'À propos', url: '/a-propos' }
      ])
    ];
    
    injectSchema(schemas);
  }, []);

  const values = [
    {
      icon: "ri-star-line",
      title: "Excellence",
      description: "Nous visons l'excellence dans chaque intervention, avec un souci du détail constant"
    },
    {
      icon: "ri-shield-check-line",
      title: "Fiabilité",
      description: "Respect des délais, engagement tenu, satisfaction garantie à chaque fois"
    },
    {
      icon: "ri-heart-line",
      title: "Passion",
      description: "Une passion pour la propreté et le bien-être de nos clients"
    },
    {
      icon: "ri-leaf-line",
      title: "Responsabilité",
      description: "Engagement écologique avec des produits respectueux de l'environnement"
    }
  ];

  const commitments = [
    {
      icon: "ri-time-line",
      title: "Ponctualité",
      description: "Nous respectons scrupuleusement les horaires convenus"
    },
    {
      icon: "ri-user-smile-line",
      title: "Service client premium",
      description: "Écoute, conseil et accompagnement personnalisé"
    },
    {
      icon: "ri-tools-line",
      title: "Équipement professionnel",
      description: "Matériel de dernière génération pour des résultats optimaux"
    },
    {
      icon: "ri-shield-star-line",
      title: "Garantie satisfaction",
      description: "Si vous n'êtes pas satisfait, nous revenons gratuitement"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/39f01b0f1c683637cd8f53dc045bcf45/b7bb771b8d681dae1702525950b7e60d.png" 
                alt="Spark'Up Logo" 
                className="h-16 w-auto"
              />
            </Link>
            <div className="hidden md:flex space-x-10">
              <Link to="/" className="text-gray-600 hover:text-navy font-medium transition-colors whitespace-nowrap cursor-pointer">
                Accueil
              </Link>
              <Link to="/services" className="text-gray-600 hover:text-navy font-medium transition-colors whitespace-nowrap cursor-pointer">
                Services
              </Link>
              <Link to="/processus" className="text-gray-600 hover:text-navy font-medium transition-colors whitespace-nowrap cursor-pointer">
                Processus
              </Link>
              <Link to="/a-propos" className="text-navy font-medium whitespace-nowrap cursor-pointer">
                À propos
              </Link>
              <Link to="/contact" className="text-gray-600 hover:text-navy font-medium transition-colors whitespace-nowrap cursor-pointer">
                Contact
              </Link>
            </div>
            <Link to="/contact" className="bg-navy text-white px-8 py-3 rounded-full font-semibold hover:bg-navy-dark transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
              Devis gratuit
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-premium-gray via-white to-sky/5 py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-sky font-semibold text-sm tracking-wider uppercase mb-4 block">Notre Histoire</span>
          <h2 className="text-5xl lg:text-7xl font-bold text-navy mb-6">À Propos de Spark'Up</h2>
          <p className="text-xl lg:text-2xl text-gray-600 font-light max-w-3xl mx-auto">
            Votre partenaire de confiance pour un nettoyage professionnel à Lausanne
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sky font-semibold text-sm tracking-wider uppercase mb-4 block">Mission</span>
              <h3 className="text-4xl lg:text-5xl font-bold text-navy mb-8">
                Notre Mission
              </h3>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed font-light">
                Chez Spark'Up, notre mission est de <strong className="text-navy">rendre la propreté professionnelle accessible à tous</strong>. Nous croyons que chacun mérite de vivre dans un environnement sain et propre, avec des meubles qui retrouvent leur éclat d'origine.
              </p>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed font-light">
                Basés à <strong className="text-navy">Lausanne</strong>, nous intervenons dans tout le <strong className="text-navy">Canton de Vaud</strong> pour offrir un service de nettoyage de canapés, fauteuils, matelas et tissus d'intérieur de qualité supérieure.
              </p>
              <p className="text-xl text-gray-600 leading-relaxed font-light">
                Notre approche combine <strong className="text-navy">expertise technique</strong>, <strong className="text-navy">équipement professionnel</strong> et <strong className="text-navy">produits écologiques</strong> pour garantir des résultats exceptionnels tout en respectant votre santé et l'environnement.
              </p>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-sky/20 to-navy/20 rounded-3xl blur-2xl"></div>
              <img 
                src="https://public.readdy.ai/ai/img_res/f8bc657f3ec8ddc2801a1f2e1d9e6239.jpg"
                alt="Équipe Spark'Up"
                className="relative rounded-3xl shadow-2xl w-full h-auto object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-premium-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="text-sky font-semibold text-sm tracking-wider uppercase mb-4 block">Valeurs</span>
            <h3 className="text-4xl lg:text-5xl font-bold text-navy mb-6">
              Nos Valeurs
            </h3>
            <p className="text-xl text-gray-600 font-light">
              Les principes qui guident notre travail au quotidien
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-10 rounded-3xl shadow-md hover:shadow-xl transition-all text-center group">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-sky to-navy text-white rounded-2xl mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${value.icon} text-3xl`}></i>
                </div>
                <h4 className="text-xl font-bold text-navy mb-3">
                  {value.title}
                </h4>
                <p className="text-gray-600 font-light leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitments Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="text-sky font-semibold text-sm tracking-wider uppercase mb-4 block">Engagements</span>
            <h3 className="text-4xl lg:text-5xl font-bold text-navy mb-6">
              Nos Engagements
            </h3>
            <p className="text-xl text-gray-600 font-light">
              Ce que nous vous garantissons à chaque intervention
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {commitments.map((commitment, index) => (
              <div key={index} className="bg-premium-gray p-10 rounded-3xl shadow-md hover:shadow-xl transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-sky to-navy text-white rounded-2xl flex-shrink-0">
                    <i className={`${commitment.icon} text-3xl`}></i>
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold text-navy mb-3">
                      {commitment.title}
                    </h4>
                    <p className="text-gray-600 text-lg font-light leading-relaxed">
                      {commitment.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-24 bg-gradient-to-br from-navy to-navy-dark text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-32 h-32 flex items-center justify-center bg-white/10 rounded-full mx-auto mb-8">
              <i className="ri-user-line text-6xl text-sky"></i>
            </div>
            <span className="text-sky font-semibold text-sm tracking-wider uppercase mb-4 block">Fondateur</span>
            <h3 className="text-4xl lg:text-5xl font-bold mb-8">
              Le Fondateur
            </h3>
            <p className="text-xl text-white/80 mb-6 leading-relaxed font-light">
              Spark'Up a été créé par un passionné de propreté et de service client, convaincu que chacun mérite de vivre dans un environnement sain et agréable.
            </p>
            <p className="text-xl text-white/80 mb-6 leading-relaxed font-light">
              Fort d'une expérience dans le nettoyage professionnel et d'une formation certifiée, le fondateur a développé une méthode unique combinant efficacité, rapidité et respect des matériaux.
            </p>
            <p className="text-xl text-white/80 leading-relaxed font-light">
              Aujourd'hui, Spark'Up est reconnu à Lausanne et dans le Canton de Vaud pour la qualité de ses prestations et la satisfaction de ses clients.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-premium-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="text-sky font-semibold text-sm tracking-wider uppercase mb-4 block">Différence</span>
            <h3 className="text-4xl lg:text-5xl font-bold text-navy mb-6">
              Pourquoi Nous Choisir ?
            </h3>
            <p className="text-xl text-gray-600 font-light">
              Ce qui fait la différence Spark'Up
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-10 rounded-3xl shadow-md hover:shadow-xl transition-all text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-sky/20 to-navy/20 rounded-full mx-auto mb-6">
                <i className="ri-medal-line text-5xl text-sky"></i>
              </div>
              <h4 className="text-2xl font-bold text-navy mb-4">Expertise Certifiée</h4>
              <p className="text-gray-600 text-lg font-light leading-relaxed">
                Formation professionnelle et certification dans le nettoyage textile
              </p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-md hover:shadow-xl transition-all text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-sky/20 to-navy/20 rounded-full mx-auto mb-6">
                <i className="ri-map-pin-line text-5xl text-sky"></i>
              </div>
              <h4 className="text-2xl font-bold text-navy mb-4">Service Local</h4>
              <p className="text-gray-600 text-lg font-light leading-relaxed">
                Basés à Lausanne, nous connaissons parfaitement la région
              </p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-md hover:shadow-xl transition-all text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-sky/20 to-navy/20 rounded-full mx-auto mb-6">
                <i className="ri-customer-service-line text-5xl text-sky"></i>
              </div>
              <h4 className="text-2xl font-bold text-navy mb-4">Disponibilité</h4>
              <p className="text-gray-600 text-lg font-light leading-relaxed">
                Intervention rapide sous 48h dans tout le Canton de Vaud
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl lg:text-5xl font-bold text-navy mb-6">
            Prêt à découvrir la différence Spark'Up ?
          </h3>
          <p className="text-xl text-gray-600 mb-10 font-light">
            Contactez-nous pour un devis gratuit et sans engagement
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-navy text-white px-10 py-4 rounded-full font-semibold text-lg hover:bg-navy-dark transition-all shadow-xl whitespace-nowrap cursor-pointer">
              Demander un devis gratuit
            </Link>
            <a href="tel:+41782036539" className="bg-white text-navy border-2 border-navy px-10 py-4 rounded-full font-semibold text-lg hover:bg-navy hover:text-white transition-all whitespace-nowrap cursor-pointer">
              Appeler maintenant
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div>
              <img 
                src="https://static.readdy.ai/image/39f01b0f1c683637cd8f53dc045bcf45/b7bb771b8d681dae1702525950b7e60d.png" 
                alt="Spark'Up Logo" 
                className="h-12 w-auto mb-6"
              />
              <p className="text-white/70 mb-6 font-light leading-relaxed">
                Nettoyage professionnel de canapés et tissus à Lausanne
              </p>
              <div className="flex gap-4">
                <a href="https://share.google/v4Ro6xy2LeuVbtJiq" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-full hover:bg-sky transition-all cursor-pointer">
                  <i className="ri-google-fill text-xl"></i>
                </a>
                <a href="https://www.instagram.com/sparkup.swiss/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-full hover:bg-sky transition-all cursor-pointer">
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-6">Navigation</h4>
              <ul className="space-y-3">
                <li><Link to="/" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">Accueil</Link></li>
                <li><Link to="/services" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">Services</Link></li>
                <li><Link to="/processus" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">Processus</Link></li>
                <li><Link to="/a-propos" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">À propos</Link></li>
                <li><Link to="/contact" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-6">Services</h4>
              <ul className="space-y-3">
                <li className="text-white/70 font-light">Nettoyage de canapés</li>
                <li className="text-white/70 font-light">Nettoyage de fauteuils</li>
                <li className="text-white/70 font-light">Nettoyage de matelas</li>
                <li className="text-white/70 font-light">Nettoyage de cuir</li>
                <li className="text-white/70 font-light">Nettoyage de tapis</li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-6">Contact</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-sky text-xl flex-shrink-0"></i>
                  <span className="text-white/70 font-light">Lausanne, Canton de Vaud, Suisse</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-phone-line text-sky text-xl"></i>
                  <a href="tel:+41782036539" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">+41 78 203 65 39</a>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-mail-line text-sky text-xl"></i>
                  <a href="mailto:Sparkup.swiss@gmail.com" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">Sparkup.swiss@gmail.com</a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-white/60 font-light">
            <p>&copy; 2025 Spark'Up. Tous droits réservés. | <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
